import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

/**
 * Configuração do QueryClient
 * Define comportamento padrão de cache e refetch
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache válido por 5 minutos (staleTime)
      staleTime: 5 * 60 * 1000,

      // Manter dados em cache por 10 minutos após ficar "stale"
      cacheTime: 10 * 60 * 1000,

      // Não refetch automaticamente ao focar janela
      refetchOnWindowFocus: false,

      // Não refetch ao reconectar
      refetchOnReconnect: false,

      // Retry em caso de erro
      retry: 1,

      // Tempo de retry
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Suspense desabilitado (usar isLoading)
      suspense: false,

      // Manter queries antigas em background
      keepPreviousData: true,
    },
    mutations: {
      // Retry em caso de erro em mutations
      retry: 0,

      // Callback de erro global (opcional)
      onError: (error) => {
        console.error('[Mutation Error]:', error);
      },
    },
  },
});

/**
 * Provider do React Query
 * Envolve a aplicação e fornece cache global de queries
 */
export const QueryProvider = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      {children}

      {/* DevTools apenas em desenvolvimento */}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools
          initialIsOpen={false}
          position="bottom-right"
          buttonPosition="bottom-right"
        />
      )}
    </QueryClientProvider>
  );
};

export default QueryProvider;

/**
 * Query Keys para organização
 * Facilita invalidação e gestão de cache
 */
export const QueryKeys = {
  // Dashboard
  DASHBOARD_STATS: 'dashboard_stats',
  DASHBOARD_INTERACTIONS: 'dashboard_interactions',
  DASHBOARD_CHARTS: 'dashboard_charts',
  DASHBOARD_ACTIVITIES: 'dashboard_activities',

  // Users
  USERS: 'users',
  USER: 'user',
  USER_STATS: 'user_stats',
  CURRENT_USER: 'current_user',

  // CRM
  CRM_CLIENTS: 'crm_clients',
  CRM_CLIENT: 'crm_client',
  CRM_STATS: 'crm_stats',

  // WhatsApp
  WHATSAPP_CONVERSATIONS: 'whatsapp_conversations',
  WHATSAPP_CONVERSATION: 'whatsapp_conversation',
  WHATSAPP_MESSAGES: 'whatsapp_messages',
  WHATSAPP_STATS: 'whatsapp_stats',
  WHATSAPP_CONTACTS: 'whatsapp_contacts',

  // IA
  IA_SETTINGS: 'ia_settings',
  IA_STATS: 'ia_stats',
  IA_MODELS: 'ia_models',

  // Documents
  DOCUMENTS: 'documents',
  DOCUMENT_STATS: 'document_stats',

  // Contatos
  CONTATOS: 'contatos',
  CONTATOS_STATS: 'contatos_stats',

  // Campaigns
  CAMPAIGNS: 'campaigns',
  CAMPAIGN: 'campaign',
  CAMPAIGN_STATUS: 'campaign_status',
  ACTIVE_CAMPAIGN: 'active_campaign',
  CAMPAIGN_MESSAGES: 'campaign_messages',
  CAMPAIGN_STATS: 'campaign_stats',

  // Analytics
  INTERACTIONS: 'interactions',
  TOTAL_INTERACTIONS: 'total_interactions',
  HUMAN_INTERVENTIONS: 'human_interventions',
  TOTAL_HUMAN_INTERVENTIONS: 'total_human_interventions',
  RESPONSE_TIME: 'response_time',

  // Notifications
  NOTIFICATIONS: 'notifications',
  UNREAD_NOTIFICATIONS: 'unread_notifications',

  // Evolution API
  EVOLUTION_INSTANCE: 'evolution_instance',
  EVOLUTION_STATUS: 'evolution_status',
  EVOLUTION_QRCODE: 'evolution_qrcode',

  // System
  SYSTEM_HEALTH: 'system_health',
};

/**
 * Configurações de staleTime por tipo de query
 * Define quanto tempo os dados são considerados "frescos"
 */
export const StaleTimeConfig = {
  // Dados em tempo real (5 segundos)
  REALTIME: 5 * 1000,

  // Dados que mudam frequentemente (30 segundos)
  FREQUENT: 30 * 1000,

  // Dados padrão (5 minutos)
  DEFAULT: 5 * 60 * 1000,

  // Dados que mudam raramente (15 minutos)
  RARE: 15 * 60 * 1000,

  // Dados quase estáticos (1 hora)
  STATIC: 60 * 60 * 1000,
};

/**
 * Helper para invalidar queries relacionadas
 */
export const invalidateQueries = (queryKey) => {
  return queryClient.invalidateQueries({ queryKey: [queryKey] });
};

/**
 * Helper para invalidar múltiplas queries
 */
export const invalidateMultipleQueries = (queryKeys) => {
  return Promise.all(
    queryKeys.map(key => queryClient.invalidateQueries({ queryKey: [key] }))
  );
};

/**
 * Helper para limpar todo o cache
 */
export const clearAllCache = () => {
  queryClient.clear();
  console.log('[QueryClient] Cache limpo');
};

/**
 * Helper para pré-carregar dados (prefetch)
 */
export const prefetchQuery = async (queryKey, queryFn, options = {}) => {
  await queryClient.prefetchQuery({
    queryKey: [queryKey],
    queryFn,
    ...options
  });
};
